import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { removeCohortMember } from "@/lib/unenrollRpc";
import { useAuth } from "@/lib/auth";

export interface Cohort {
  id: string;
  name: string;
  curriculum_id: string;
  created_by: string | null;
  start_date: string | null;
  end_date: string | null;
  is_active: boolean;
  gating_enabled: boolean;
  created_at: string;
  updated_at: string;
  curriculum_title?: string;
  member_count?: number;
}

export interface CohortMember {
  user_id: string;
  full_name: string;
  avatar_url: string | null;
  enrolled_courses: number;
  total_courses: number;
  completed_modules: number;
  total_modules: number;
  progress_percent: number;
}


export interface CohortModuleAccess {
  id: string;
  cohort_id: string;
  module_id: string;
  unlocked_at: string | null;
  unlocked_by: string | null;
}

export interface CohortCourseModules {
  course_id: string;
  course_title: string;
  modules: {
    id: string;
    title: string;
    sequence_order: number;
    is_unlocked: boolean;
    unlocked_at: string | null;
  }[];
}

export function useInstructorCohorts() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["instructor-cohorts"],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from("cohorts")
        .select("*, curricula(title)")
        .order("created_at", { ascending: false });

      if (error) throw error;

      const cohortsWithCounts = await Promise.all(
        (data || []).map(async (cohort) => {
          const { count } = await supabase
            .from("course_enrollments")
            .select("*", { count: "exact", head: true })
            .eq("cohort_id", cohort.id);

          return {
            ...cohort,
            curriculum_title: (cohort.curricula as any)?.title || "Unknown",
            member_count: count || 0,
          } as Cohort;
        })
      );

      return cohortsWithCounts;
    },
    enabled: !!user,
  });
}

export function useCohort(cohortId: string) {
  return useQuery({
    queryKey: ["cohort", cohortId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cohorts")
        .select("*, curricula(title)")
        .eq("id", cohortId)
        .single();

      if (error) throw error;
      return {
        ...data,
        curriculum_title: (data.curricula as any)?.title || "Unknown",
      } as Cohort;
    },
    enabled: !!cohortId,
  });
}

export function useCohortMembers(cohortId: string, curriculumId: string) {
  return useQuery({
    queryKey: ["cohort-members", cohortId],
    queryFn: async () => {
      // Get all enrollments for this cohort
      const { data: enrollments, error } = await supabase
        .from("course_enrollments")
        .select("id, user_id, course_id")
        .eq("cohort_id", cohortId);

      if (error) throw error;
      if (!enrollments || enrollments.length === 0) return [];

      // Get unique user IDs
      const userIds = [...new Set(enrollments.map((e) => e.user_id))];
      const courseIds = [...new Set(enrollments.map((e) => e.course_id))];
      const enrollmentIds = enrollments.map((e) => e.id);

      // Get total courses in curriculum
      const { data: courseLinks } = await supabase
        .from("curriculum_courses")
        .select("course_id")
        .eq("curriculum_id", curriculumId);
      const totalCourses = courseLinks?.length || 0;

      // Get profiles
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, full_name, avatar_url")
        .in("id", userIds);

      const profileMap = new Map(
        (profiles || []).map((p) => [p.id, p])
      );

      // Get modules per course (for computing total assigned modules per user)
      const { data: modules } = await supabase
        .from("modules")
        .select("id, course_id")
        .in("course_id", courseIds.length > 0 ? courseIds : ["none"]);

      const modulesByCourse = new Map<string, string[]>();
      (modules || []).forEach((m: any) => {
        const arr = modulesByCourse.get(m.course_id) || [];
        arr.push(m.id);
        modulesByCourse.set(m.course_id, arr);
      });

      // Get completed module_progress for these enrollments
      const { data: progress } = await supabase
        .from("module_progress")
        .select("enrollment_id, completed_at")
        .in("enrollment_id", enrollmentIds.length > 0 ? enrollmentIds : ["none"])
        .not("completed_at", "is", null);

      const completedByEnrollment = new Map<string, number>();
      (progress || []).forEach((p: any) => {
        completedByEnrollment.set(
          p.enrollment_id,
          (completedByEnrollment.get(p.enrollment_id) || 0) + 1
        );
      });

      // Per-user totals
      const userCourseCounts = new Map<string, number>();
      const userTotalModules = new Map<string, number>();
      const userCompletedModules = new Map<string, number>();
      enrollments.forEach((e) => {
        userCourseCounts.set(e.user_id, (userCourseCounts.get(e.user_id) || 0) + 1);
        const moduleCount = (modulesByCourse.get(e.course_id) || []).length;
        userTotalModules.set(
          e.user_id,
          (userTotalModules.get(e.user_id) || 0) + moduleCount
        );
        userCompletedModules.set(
          e.user_id,
          (userCompletedModules.get(e.user_id) || 0) + (completedByEnrollment.get(e.id) || 0)
        );
      });

      return userIds.map((userId) => {
        const profile = profileMap.get(userId);
        const completed = userCompletedModules.get(userId) || 0;
        const total = userTotalModules.get(userId) || 0;
        return {
          user_id: userId,
          full_name: profile?.full_name || "Unknown",
          avatar_url: profile?.avatar_url || null,
          enrolled_courses: userCourseCounts.get(userId) || 0,
          total_courses: totalCourses,
          completed_modules: completed,
          total_modules: total,
          progress_percent: total > 0 ? Math.round((completed / total) * 100) : 0,
        } as CohortMember;
      });
    },
    enabled: !!cohortId && !!curriculumId,
  });
}


export function useCohortModuleAccess(
  cohortId: string,
  curriculumId: string
) {
  return useQuery({
    queryKey: ["cohort-module-access", cohortId],
    queryFn: async (): Promise<CohortCourseModules[]> => {
      // Get courses in this curriculum
      const { data: courseLinks } = await supabase
        .from("curriculum_courses")
        .select("course_id")
        .eq("curriculum_id", curriculumId);

      if (!courseLinks || courseLinks.length === 0) return [];

      const courseIds = courseLinks.map((l) => l.course_id);

      // Get courses with titles
      const { data: courses } = await supabase
        .from("courses")
        .select("id, title")
        .in("id", courseIds)
        .order("created_at", { ascending: true });

      if (!courses || courses.length === 0) return [];

      // Get all modules for these courses
      const { data: modules } = await supabase
        .from("modules")
        .select("id, course_id, title, sequence_order")
        .in("course_id", courseIds)
        .order("sequence_order", { ascending: true });

      // Get cohort_module_access records
      const { data: accessRecords } = await supabase
        .from("cohort_module_access")
        .select("*")
        .eq("cohort_id", cohortId);

      const accessMap = new Map(
        (accessRecords || []).map((a) => [a.module_id, a])
      );

      return courses.map((course) => ({
        course_id: course.id,
        course_title: course.title,
        modules: (modules || [])
          .filter((m) => m.course_id === course.id)
          .map((m) => ({
            id: m.id,
            title: m.title,
            sequence_order: m.sequence_order,
            is_unlocked: !!accessMap.get(m.id)?.unlocked_at,
            unlocked_at: accessMap.get(m.id)?.unlocked_at || null,
          })),
      }));
    },
    enabled: !!cohortId && !!curriculumId,
  });
}

export function useAddCohortMember() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      cohortId,
      userId,
      curriculumId,
      gatingEnforced,
    }: {
      cohortId: string;
      userId: string;
      curriculumId: string;
      gatingEnforced: boolean;
    }) => {
      if (!user) throw new Error("Must be logged in");

      const { data, error } = await supabase.rpc("admin_add_cohort_member", {
        _cohort_id: cohortId,
        _user_id: userId,
      });

      if (error) {
        console.error("admin_add_cohort_member error:", error);
        throw error;
      }

      const rows = (data || []) as { course_id: string; action: string }[];

      const courseIds = rows.map((r) => r.course_id);
      const { data: courses } = await supabase
        .from("courses")
        .select("id, title")
        .in("id", courseIds.length > 0 ? courseIds : ["00000000-0000-0000-0000-000000000000"]);
      const courseNameMap = new Map((courses || []).map((c) => [c.id, c.title]));

      const enrolledCourses: string[] = [];
      const skippedCourses: string[] = [];
      for (const r of rows) {
        const name = courseNameMap.get(r.course_id) || r.course_id;
        if (r.action === "enrolled" || r.action === "promoted" || r.action === "already_in_cohort") {
          enrolledCourses.push(name);
        } else {
          skippedCourses.push(name);
        }
      }

      // suppress unused vars from the removed branch signature
      void curriculumId;
      void gatingEnforced;

      return { skippedCourses, enrolledCourses };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["cohort-members", variables.cohortId],
      });
      queryClient.invalidateQueries({
        queryKey: ["instructor-cohorts"],
      });
    },
  });
}

export function useRemoveCohortMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      cohortId,
      userId,
    }: {
      cohortId: string;
      userId: string;
    }) => {
      // Atomic server-side removal. A direct DELETE here silently no-opped:
      // course_enrollments had no DELETE policy, so RLS filtered it out and
      // PostgREST returned 200 with 0 rows and error === null — the member
      // stayed in the cohort while the UI reported success.
      await removeCohortMember(cohortId, userId);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["cohort-members", variables.cohortId],
      });
      queryClient.invalidateQueries({
        queryKey: ["instructor-cohorts"],
      });
    },
  });
}

export function useToggleModuleAccess() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      cohortId,
      moduleId,
      unlock,
    }: {
      cohortId: string;
      moduleId: string;
      unlock: boolean;
    }) => {
      if (!user) throw new Error("Must be logged in");

      if (unlock) {
        const { error } = await supabase
          .from("cohort_module_access")
          .upsert(
            {
              cohort_id: cohortId,
              module_id: moduleId,
              unlocked_at: new Date().toISOString(),
              unlocked_by: user.id,
            },
            { onConflict: "cohort_id,module_id" }
          );
        if (error) throw error;
      } else {
        // Lock = remove the access record
        const { error } = await supabase
          .from("cohort_module_access")
          .delete()
          .eq("cohort_id", cohortId)
          .eq("module_id", moduleId);
        if (error) throw error;
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["cohort-module-access", variables.cohortId],
      });
    },
  });
}

export function useCohortsByCurriculum(curriculumId: string | null) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["cohorts-by-curriculum", curriculumId],
    queryFn: async () => {
      if (!curriculumId || !user) return [];

      const { data, error } = await supabase
        .from("cohorts")
        .select(`
          id,
          name,
          start_date,
          end_date,
          is_active,
          gating_enabled,
          created_at
        `)
        .eq("curriculum_id", curriculumId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!curriculumId && !!user,
  });
}

// Hook to check if current user has a gated cohort enrollment for a given course
export function useGatedCohortCheck(courseId: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["gated-cohort-check", courseId, user?.id],
    queryFn: async () => {
      if (!user) return null;

      const { data, error } = await supabase
        .from("course_enrollments")
        .select("id, cohort_id, gating_enforced")
        .eq("user_id", user.id)
        .eq("course_id", courseId)
        .eq("gating_enforced", true)
        .not("cohort_id", "is", null)
        .maybeSingle();

      if (error) throw error;
      return data; // null if no gated enrollment
    },
    enabled: !!user && !!courseId,
  });
}

export function useCreateCohort() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (cohort: {
      name: string;
      curriculum_id: string;
      start_date: string;
      end_date?: string | null;
      gating_enabled: boolean;
    }) => {
      if (!user) throw new Error("Must be logged in");

      const { data, error } = await supabase
        .from("cohorts")
        .insert({
          ...cohort,
          created_by: user.id,
          is_active: true,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["instructor-cohorts"] });
    },
  });
}

export function useUpdateCohort() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      ...updates
    }: Partial<Cohort> & { id: string }) => {
      const { data, error } = await supabase
        .from("cohorts")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;

      if (updates.gating_enabled !== undefined) {
        const { error: enrollErr } = await supabase
          .from("course_enrollments")
          .update({ gating_enforced: updates.gating_enabled })
          .eq("cohort_id", id);
        if (enrollErr) throw enrollErr;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["instructor-cohorts"] });
      queryClient.invalidateQueries({ queryKey: ["cohort-module-gating"] });
    },
  });
}

export function useDeleteCohort() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (cohortId: string) => {
      const { error } = await supabase
        .from("cohorts")
        .delete()
        .eq("id", cohortId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["instructor-cohorts"] });
    },
  });
}
